import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { useDebouncedState } from '~/eds/src/hooks/useDebouncedState';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { Table } from '~/eds/src/modules/Table/Table';
import { TableContextType } from '~/enums/TableContextType';
import { ClientModal } from '~/features/admin/ClientModal';
import { INITIAL_PAGE_SIZE } from '~/features/admin/constants';
import { Client, ClientAction } from '~/features/admin/types';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useTableSettings } from '~/hooks/useTableSettings';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { RoutePathType } from '~/routing/enums';
import { generateNav } from '~/routing/nav';
import { useRouting } from '~/routing/useRouting';

const Page = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { client } = useCurrentUser();
  const { navigate } = useRouting();

  const nav = generateNav({
    current: {
      text: intl.formatMessage({
        id: 'AdminPage.adminNavText',
        defaultMessage: 'Admin',
        description: 'Navigation text for admin page',
      }),
      pathname: RoutePathType.Admin,
    },
    from: RoutePathType.AdminConsoleClient,
    params: {
      clientId: client,
    },
    intl,
  });

  const columns = [
    {
      key: 'name',
      cellType: 'link',
      title: intl.formatMessage({
        id: 'AdminPage.nameColumnTitle',
        defaultMessage: 'Name',
        description: 'Column title for client name',
      }),
      mapCellProps: (client: Client) => ({
        id: `admin_clientsRow_name?client=${client.id}`,
        pathname: `/admin/console/${client.id}/`,
        text: client.name,
      }),
    },
    {
      key: 'bucket',
      cellType: 'text',
      title: intl.formatMessage({
        id: 'AdminPage.bucketColumnTitle',
        defaultMessage: 'Bucket',
        description: 'Column title for bucket',
      }),
      mapCellProps: (client: Client) => ({
        text: client.bucket,
      }),
    },
    {
      key: 'index',
      cellType: 'text',
      title: intl.formatMessage({
        id: 'AdminPage.indexColumnTitle',
        defaultMessage: 'Index',
        description: 'Column title for index',
      }),
      mapCellProps: (client: Client) => ({
        text: client.index,
      }),
    },
    {
      key: 'domain',
      cellType: 'text',
      title: intl.formatMessage({
        id: 'AdminPage.domainColumnTitle',
        defaultMessage: 'Domain',
        description: 'Column title for domain',
      }),
      mapCellProps: (client: Client) => ({
        text: client.subDomain,
      }),
    },
    {
      key: 'client_type',
      cellType: 'text',
      title: intl.formatMessage({
        id: 'AdminPage.clientTypeColumnTitle',
        defaultMessage: 'Client type',
        description: 'Column title for client type',
      }),
      mapCellProps: (client: Client) => ({
        text: client.clientType,
      }),
    },
    {
      key: 'doc_count',
      cellType: 'number',
      title: intl.formatMessage({
        id: 'AdminPage.totalActiveDocCountColumnTitle',
        defaultMessage: 'Total active doc count',
        description: 'Column title for total active document count',
      }),
      mapCellProps: (client: Client) => ({
        number: client.docCount,
      }),
    },
    {
      key: 'pre_sig_doc_count',
      cellType: 'number',
      title: intl.formatMessage({
        id: 'AdminPage.preSigDocCountColumnTitle',
        defaultMessage: 'Pre-sig doc count',
        description: 'Column title for pre-signature document count',
      }),
      mapCellProps: (client: Client) => ({
        number: client.preSigDocCount,
      }),
    },
    {
      key: 'postSigDocCount',
      cellType: 'number',
      title: intl.formatMessage({
        id: 'AdminPage.postSigDocCountColumnTitle',
        defaultMessage: 'Post-sig doc count',
        description: 'Column title for post-signature document count',
      }),
      mapCellProps: (client: Client) => ({
        number: client.postSigDocCount,
      }),
    },
    {
      key: 'restorationHub',
      cellType: 'number',
      title: intl.formatMessage({
        id: 'AdminPage.restorationHubCountColumnTitle',
        defaultMessage: 'Restoration hub count',
        description: 'Column title for restoration hub count',
      }),
      mapCellProps: (client: Client) => ({
        number: client.restorationHubCount,
      }),
    },
    {
      key: 'billableDocs',
      cellType: 'number',
      title: intl.formatMessage({
        id: 'AdminPage.billableDocCountColumnTitle',
        defaultMessage: 'Billable doc count',
        description: 'Column title for billable document count',
      }),
      mapCellProps: (client: Client) => ({
        number: client.billableDocCount,
      }),
    },
    {
      key: 'docLimit',
      cellType: 'number',
      title: intl.formatMessage({
        id: 'AdminPage.docLimitColumnTitle',
        defaultMessage: 'Doc limit',
        description: 'Column title for document limit',
      }),
      mapCellProps: (client: Client) => ({
        number: client.docLimit,
      }),
    },
  ];

  const {
    tableSettings = {
      pageSize: INITIAL_PAGE_SIZE,
    },
  } = useTableSettings(TableContextType.Clients);

  const [page, setPage] = useState(1);
  const [debouncedSearchText, searchText, setSearchText] =
    useDebouncedState('');

  const {
    data: clients = {
      count: 0,
      results: [],
    },
    isLoading,
    isFetching,
    isError,
  } = api.endpoints.getClients.useQuery({
    page,
    searchText: debouncedSearchText,
  });

  const onPaginate = useCallback(
    ({ pageIndex }: { pageIndex: number }) => {
      setPage(pageIndex);
    },
    [page],
  );

  const handleAction = (action: ClientAction, client: Client) => {
    if (action === 'viewCredits') {
      navigate(`${client.id}/credits`);
    } else {
      dispatch(
        slices.clients.actions.setConfiguration({
          action,
          client,
          configs: {
            ...client,
            featureFlagFields: client.featureFlagMappedFields?.map((f) => f.id),
          },
        }),
      );
    }
  };

  const handleSearchText = (text: string) => {
    setPage(1);
    setSearchText(text);
  };

  return (
    <PageLayout
      title={intl.formatMessage({
        id: 'AdminPage.pageTitle',
        defaultMessage: 'Manage Clients',
        description: 'Page title for admin clients management',
      })}
      nav={nav}
      actions={[
        {
          text: intl.formatMessage({
            id: 'AdminPage.addNewClientButton',
            defaultMessage: 'Add New Client',
            description: 'Button text to add a new client',
          }),
          level: 'primary',
          onClick: () => {
            dispatch(slices.clients.actions.setAction('add'));
          },
        },
        {
          text: intl.formatMessage({
            id: 'AdminPage.checkBucketButton',
            defaultMessage: 'Check Bucket',
            description: 'Button text to check bucket',
          }),
          onClick: () => {
            dispatch(slices.clients.actions.setAction('checkBucket'));
          },
        },
      ]}
    >
      <ContentContainer
        loadingContent={{
          isLoading,
          message: intl.formatMessage({
            id: 'AdminPage.loadingClientsMessage',
            defaultMessage: 'Loading clients...',
            description: 'Loading message while clients are being fetched',
          }),
        }}
        placeholderContent={
          isError
            ? {
                message: intl.formatMessage({
                  id: 'AdminPage.errorLoadingClients',
                  defaultMessage: 'Error loading clients',
                  description: 'Error message when clients fail to load',
                }),
              }
            : undefined
        }
      >
        <Table
          name={intl.formatMessage({
            id: 'AdminPage.clientsTableName',
            defaultMessage: 'Clients',
            description: 'Name for the clients table',
          })}
          isLoading={isFetching}
          data={clients.results}
          columns={columns}
          options={{
            enableExportXlsx: false,
            enableManageColumns: false,
            enableSearch: true,
            enablePagination: true,
            enableSelectRows: false,
          }}
          state={{
            pageIndex: page,
            pageSize: tableSettings.pageSize,
          }}
          onPaginate={onPaginate}
          searchText={searchText}
          onSearch={handleSearchText}
          rowActions={[
            {
              label: intl.formatMessage({
                id: 'AdminPage.editClientAction',
                defaultMessage: 'Edit Client',
                description: 'Row action to edit a client',
              }),
              onClick: (client: Client) => handleAction('edit', client),
            },
            {
              label: intl.formatMessage({
                id: 'AdminPage.deleteClientAction',
                defaultMessage: 'Delete Client',
                description: 'Row action to delete a client',
              }),
              onClick: (client: Client) => handleAction('delete', client),
            },
            {
              label: intl.formatMessage({
                id: 'AdminPage.viewCreditsAction',
                defaultMessage: 'View Credits',
                description: 'Row action to view client credits',
              }),
              onClick: (client: Client) => handleAction('viewCredits', client),
            },
          ]}
          totalCount={clients.count}
        />
      </ContentContainer>
      <ClientModal />
    </PageLayout>
  );
};

export default Page;
